import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Checkbox,
  CheckIcon,
  CloseIcon,
  EditIcon,
  FileUploader,
  FormControl,
  HeadingSmall,
  Input,
  LabelMedium,
  Modal,
  ParagraphSmall,
  ProgressBarCircular,
  ResetIcon,
  StyleObject,
  Tokens,
  useSnackbar,
  useStyletron,
} from '@visualfabriq/vf-ui-kit';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BulkImportJobStatus, PipelinesApi } from 'src/api-new/bifrost';
import { Show } from 'src/components';
import { useInstances } from 'src/components/hooks/useInstances';
import { List, VirtualList } from 'src/components/list';
import { useBifrostApi } from 'src/services/useBifrostApi';
import { hashPipeline, pipelineDataAtom, pipelineImportsAtom, resetPipelineImportsAtom } from './use-worker';

type Action = 'next' | 'prev' | 'reset' | { to: number | string };

type PipelineSelectionAction =
  | { type: 'toggle_all'; pipelines: string[] }
  | { type: 'toggle_one'; pipeline: string }
  | { type: 'reset' };

const baseSelectionAtom = atom(new Set<string>());

const pipelineSelectionAtom = atom(
  (get) => get(baseSelectionAtom),
  (get, set, action: PipelineSelectionAction) => {
    const state = get(baseSelectionAtom);
    const next = new Set(state);

    switch (action.type) {
      case 'toggle_all': {
        const allSelected = action.pipelines.length > 0 && action.pipelines.every((p) => next.has(p));
        for (const p of action.pipelines) {
          if (allSelected) next.delete(p);
          else next.add(p);
        }
        break;
      }
      case 'toggle_one': {
        if (next.has(action.pipeline)) next.delete(action.pipeline);
        else next.add(action.pipeline);
        break;
      }
      case 'reset': {
        set(baseSelectionAtom, new Set());
        return;
      }
    }

    set(baseSelectionAtom, next);
  },
);

function useSteps<T extends Array<string>>(steps: T) {
  const useStepsReducer = useMemo(
    () =>
      function (state: number, action: Action): number {
        switch (action) {
          case 'next':
            return state + 1;
          case 'prev':
            return state - 1;
          case 'reset':
            return 0;
          default:
            if (typeof action.to === 'number') return action.to;
            return state;
        }
      },
    [],
  );
  const [index, dispatch] = useReducer(useStepsReducer, 0);
  const current = steps[index];

  return [current, dispatch] as const;
}

const StepsContext = createContext<ReturnType<typeof useSteps>>(null as never);

const pipelinesRenamedAtom = atom(new Map<string, string>());

const bodyStyles = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
} satisfies StyleObject;

const footerStyles = {
  display: 'flex',
  alignItems: 'center',
  padding: Tokens.ref.sizing.scale600,
  borderTop: `1px solid ${Tokens.sys.border.borderOpaque}`,
} satisfies StyleObject;

function Upload() {
  const [css] = useStyletron();
  const [state, setFiles] = useAtom(pipelineImportsAtom);
  const [_, dispatch] = useContext(StepsContext);
  const instanceName = useParams().instanceName;
  const navigate = useNavigate();

  const fileRowStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Tokens.ref.sizing.scale300,
    paddingBlock: Tokens.ref.sizing.scale300,
    paddingInline: Tokens.ref.sizing.scale300,
    borderRadius: Tokens.ref.border.radiusSmall,
    backgroundColor: Tokens.sys.background.backgroundPrimary,
    border: `1px solid ${Tokens.sys.border.borderOpaque}`,
  });

  const fileMetaStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: Tokens.ref.sizing.scale100,
    flex: '1 1 auto',
    minWidth: 0,
  });

  const fileNameStyles = css({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  });

  return (
    <>
      <div className={css(bodyStyles)}>
        <div
          className={css({
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            gap: Tokens.ref.sizing.scale600,
            padding: Tokens.ref.sizing.scale600,
          })}
        >
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: Tokens.ref.sizing.scale100,
              padding: Tokens.ref.sizing.scale500,
              borderRadius: Tokens.ref.border.radiusSmall,
              border: `1px solid ${Tokens.sys.border.borderOpaque}`,
            })}
          >
            <LabelMedium marginBottom="0">Target Instance</LabelMedium>
            <ParagraphSmall marginBottom="0">{instanceName}</ParagraphSmall>
          </div>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: Tokens.ref.sizing.scale400,
              padding: Tokens.ref.sizing.scale500,
              borderRadius: Tokens.ref.border.radiusSmall,
              border: `1px solid ${Tokens.sys.border.borderOpaque}`,
            })}
          >
            <FormControl label="Pipelines" caption="Upload a file containing exported pipelines">
              <FileUploader
                onDrop={(e) => {
                  setFiles((prev) => {
                    const fileMap = new Map([
                      ...prev.map((f) => [f.name, f] as const),
                      ...e.map((f) => [f.name, f] as const),
                    ]);
                    return Array.from(fileMap.values());
                  });
                }}
              />
            </FormControl>
            <Show when={state.files.length > 0}>
              <div className={css({ display: 'flex', flexDirection: 'column', gap: Tokens.ref.sizing.scale300 })}>
                <div
                  className={css({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: Tokens.ref.sizing.scale300,
                  })}
                >
                  <ParagraphSmall marginBottom="0">{state.files.length} file(s) selected</ParagraphSmall>
                </div>
                <List
                  items={state.files}
                  renderItem={(f) => (
                    <div className={fileRowStyles} title={f.file}>
                      <div className={fileMetaStyles}>
                        <ParagraphSmall marginBottom="0" className={fileNameStyles} aria-label={f.file}>
                          {f.file}
                        </ParagraphSmall>
                      </div>
                      <Show when={f.status !== 'idle'}>
                        <Badge kind={f.status === 'error' ? 'red' : 'green'} invert>
                          {f.status}
                        </Badge>
                      </Show>
                      <Button
                        kind="tertiary"
                        size="mini"
                        shape="circle"
                        aria-label={`Remove ${f.file}`}
                        onClick={() => setFiles((prev) => prev.filter((file) => file.name !== f.file))}
                      >
                        <CloseIcon />
                      </Button>
                    </div>
                  )}
                />
              </div>
            </Show>
          </div>
        </div>
      </div>
      <div className={css(footerStyles)}>
        <Button kind="secondary" onClick={() => navigate('..')}>
          Cancel
        </Button>
        <div className={css({ marginLeft: 'auto' })}>
          <Button
            disabled={state.files.some((e) => e.status === 'error') || !state.files.length}
            kind="primary"
            type="button"
            onClick={() => dispatch('next')}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
}

const UploadedPipeLine = memo(function UploadedPipeLine(props: { id: string; name: string; isDuplicate: boolean }) {
  const [css] = useStyletron();
  const [draft, setDraft] = useState('');
  const [renamed, setRenamed] = useAtom(pipelinesRenamedAtom);
  const [editing, setEditing] = useState(false);
  const isSelected = useAtomValue(useMemo(() => atom((get) => get(baseSelectionAtom).has(props.id)), [props.id]));
  const dispatch = useSetAtom(pipelineSelectionAtom);
  const hoverRef = useRef(false);
  const editBtnRef = useRef<HTMLDivElement | null>(null);
  const editBtnCallbackRef = useCallback((el: HTMLDivElement | null) => {
    editBtnRef.current = el;
    if (el && hoverRef.current) {
      el.style.visibility = 'visible';
    }
  }, []);

  const displayName = renamed.get(props.id) ?? props.name;

  const renameActionBaseStyles = css({});

  function commitRename({ name, id }: { name: string; id: string }) {
    const next = name.trim();
    const finalName = next.length ? next : props.name;

    // If no actual change vs original, don't mark as modified
    if (finalName === props.name) {
      setRenamed((prev) => {
        if (!prev.has(id)) return prev;
        const copy = new Map(prev);
        copy.delete(id);
        return copy;
      });
      setEditing(false);
      return;
    }

    // If no change vs current displayName, do nothing
    if (finalName === displayName) {
      setEditing(false);
      return;
    }

    setRenamed((prev) => {
      const copy = new Map(prev);
      copy.set(id, finalName);
      return copy;
    });

    setEditing(false);
  }

  function cancelRename() {
    setDraft(displayName);
    setEditing(false);
  }

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: Tokens.ref.border.radiusSmall,
        gap: Tokens.ref.sizing.scale400,
        border: `1px solid ${Tokens.sys.border.borderOpaque}`,
        padding: Tokens.ref.sizing.scale400,
      })}
      onPointerEnter={() => {
        hoverRef.current = true;
        if (editBtnRef.current) editBtnRef.current.style.visibility = 'visible';
      }}
      onPointerLeave={() => {
        hoverRef.current = false;
        if (editBtnRef.current) editBtnRef.current.style.visibility = 'hidden';
      }}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: Tokens.ref.sizing.scale400,
          flex: '1 1 auto',
          height: '2rem',
          minWidth: 0,
        })}
      >
        <Checkbox
          name={props.name}
          checked={isSelected}
          onChange={() => dispatch({ type: 'toggle_one', pipeline: props.id })}
        />
        <Show
          when={editing}
          fallback={
            <div className={css({ display: 'flex', alignItems: 'center', gap: Tokens.ref.sizing.scale200 })}>
              {displayName}{' '}
              <div ref={editBtnCallbackRef} className={renameActionBaseStyles} style={{ visibility: 'hidden' }}>
                <Button
                  kind="tertiary"
                  shape="circle"
                  onClick={() => {
                    setDraft(displayName);
                    setEditing(true);
                  }}
                >
                  <EditIcon />
                </Button>
              </div>
            </div>
          }
        >
          <Input
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename({ id: props.id, name: draft });
              }
              if (e.key === 'Escape') {
                e.stopPropagation();
                e.preventDefault();
                cancelRename();
              }
            }}
          />
          <Show when={editing}>
            <Button shape="circle" kind="tertiary" onClick={() => commitRename({ id: props.id, name: draft })}>
              <CheckIcon color={Tokens.sys.content.contentPositive} />
            </Button>
            <Button shape="circle" kind="tertiary" onClick={() => cancelRename()}>
              <CloseIcon />
            </Button>
          </Show>
        </Show>
        <Show when={!editing}>
          <div
            className={css({
              marginInlineStart: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: Tokens.ref.sizing.scale200,
            })}
          >
            <Show when={props.isDuplicate}>
              <Badge invert kind="red">
                Duplicate
              </Badge>
            </Show>
            <Show when={renamed.has(props.id) && !props.isDuplicate}>
              <Badge invert kind="orange">
                Modified
              </Badge>
            </Show>
            <Show when={renamed.has(props.id)}>
              <Button
                kind="tertiary"
                shape="circle"
                onClick={() =>
                  setRenamed((prev) => {
                    prev.delete(props.id);
                    return new Map(prev);
                  })
                }
              >
                <ResetIcon />
              </Button>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
});

const ReviewPipelineList = memo(function ReviewPipelineList(props: {
  items: Array<{ id: string; name: string }>;
  duplicateIds: Set<string>;
}) {
  const [css] = useStyletron();

  const listStyles = css({
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    flexDirection: 'column',
    gap: Tokens.ref.sizing.scale200,
    overflowY: 'auto',
  });

  return (
    <VirtualList
      items={props.items}
      estimateSize={54}
      gap={12}
      className={listStyles}
      getItemKey={(pipeline) => pipeline.id}
    >
      {(pipeline) => <UploadedPipeLine {...pipeline} isDuplicate={props.duplicateIds.has(pipeline.id)} />}
    </VirtualList>
  );
});

function Review() {
  const [css] = useStyletron();
  const [_, dispatchStep] = useContext(StepsContext);

  const pipelinesApi = useBifrostApi(PipelinesApi);
  const navigate = useNavigate();
  const state = useAtomValue(pipelineImportsAtom);
  const pipelineData = useAtomValue(pipelineDataAtom);
  const [q, setQuery] = useState('');
  const renamed = useAtomValue(pipelinesRenamedAtom);
  const filtered = useMemo(
    () =>
      state.pipelines.filter((pipeline) => {
        const displayName = renamed.get(pipeline.id) ?? pipeline.name ?? '';
        return displayName.toLocaleLowerCase().includes(q.toLocaleLowerCase());
      }),
    [q, renamed, state.pipelines],
  );

  const instance = useInstances();
  const snackbar = useSnackbar();
  const [selected, dispatch] = useAtom(pipelineSelectionAtom);

  // Compute which pipeline IDs have duplicate display names
  const duplicateIds = useMemo(() => {
    const nameCount = new Map<string, string[]>();
    for (const pipeline of state.pipelines) {
      const displayName = renamed.get(pipeline.id) ?? pipeline.name;
      const ids = nameCount.get(displayName) ?? [];
      ids.push(pipeline.id);
      nameCount.set(displayName, ids);
    }
    const duplicates = new Set<string>();
    for (const ids of nameCount.values()) {
      if (ids.length > 1) {
        for (const id of ids) {
          duplicates.add(id);
        }
      }
    }
    return duplicates;
  }, [renamed, state.pipelines]);

  // Check if any selected pipelines are duplicates
  const hasSelectedDuplicates = useMemo(() => {
    for (const id of selected) {
      if (duplicateIds.has(id)) {
        return true;
      }
    }
    return false;
  }, [selected, duplicateIds]);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      // Use cached pipeline data instead of stream for import
      const allPipelines = pipelineData;
      const body = allPipelines
        .filter((p) => {
          // Use deterministic hash of pipeline data as ID
          const pipelineId = hashPipeline(p);
          return selected.has(pipelineId);
        })
        .map((p) => {
          // Use deterministic hash to look up renamed pipelines
          const pipelineId = hashPipeline(p);
          const newName = renamed.get(pipelineId);
          if (newName) {
            return { ...p, name: newName };
          }
          return p;
        });

      const response = await pipelinesApi.bulkImportPipelines({
        bulkImportRequest: {
          conflict_resolution: 'ignore',
          instance_id: instance.selectedInstance.id,
          pipelines: body as never,
        },
      });

      const jobId = response.data.job_id;
      const instanceId = instance.selectedInstance.id;

      // Poll for job completion
      const pollInterval = 1000;
      const maxAttempts = 300; // 5 minutes max

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const statusResponse = await pipelinesApi.getBulkImportJobStatus({ jobId, instanceId });
        const status = statusResponse.data.status;

        if (status === BulkImportJobStatus.Completed) {
          return statusResponse.data;
        }

        if (status === BulkImportJobStatus.Failed) {
          const errors = statusResponse.data.errors;
          throw new Error(errors.length > 0 ? errors.join(', ') : 'Import job failed');
        }

        // Wait before polling again
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }

      throw new Error('Import job timed out');
    },
    onSuccess: (data) => {
      const message =
        data.failed > 0 ? `Import completed: ${data.imported} imported, ${data.failed} failed` : 'Import Successful';
      snackbar.enqueueSuccessSnackbar(message);
      navigate('..');
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Error Importing pipelines';
      snackbar.enqueueErrorSnackbar(message);
    },
  });

  const pipelineCount = state.pipelines.length;
  const allSelected = pipelineCount > 0 && pipelineCount === selected.size;
  const someSelected = pipelineCount > 0 && selected.size > 0 && !allSelected;

  const containerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: '1 1 auto',
    overflow: 'hidden',
    paddingBlockStart: Tokens.ref.sizing.scale600,
  });

  const panelStyles = css({
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: Tokens.ref.sizing.scale400,
    marginInline: Tokens.ref.sizing.scale600,
    marginBlockEnd: 0,
    padding: Tokens.ref.sizing.scale500,
    borderTopLeftRadius: Tokens.ref.border.radiusSmall,
    borderTopRightRadius: Tokens.ref.border.radiusSmall,
    border: `1px solid ${Tokens.sys.border.borderOpaque}`,
    borderBlockEnd: 0,
    overflow: 'hidden',
  });

  return (
    <>
      <div className={css(bodyStyles)}>
        <div className={containerStyles}>
          <div className={panelStyles}>
            <div
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                gap: Tokens.ref.sizing.scale400,
              })}
            >
              <Input placeholder="Search pipelines by name..." value={q} onChange={(e) => setQuery(e.target.value)} />
            </div>

            <div
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingInline: Tokens.ref.sizing.scale400,
                paddingBlock: Tokens.ref.sizing.scale200,
                borderBottom: `1px solid ${Tokens.sys.border.borderOpaque}`,
              })}
            >
              <Checkbox
                checked={allSelected}
                isIndeterminate={someSelected}
                onChange={() => {
                  dispatch({
                    type: 'toggle_all',
                    pipelines: state.pipelines.map((p) => p.id),
                  });
                }}
              >
                Select all
              </Checkbox>
              <ParagraphSmall marginBottom="0">{`${selected.size} pipelines selected`}</ParagraphSmall>
            </div>

            <ReviewPipelineList items={filtered} duplicateIds={duplicateIds} />
          </div>
        </div>
      </div>

      <div className={css(footerStyles)}>
        <Button kind="secondary" disabled={isPending} onClick={() => dispatchStep('prev')}>
          Back
        </Button>
        <div
          className={css({
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: Tokens.ref.sizing.scale400,
          })}
        >
          <Show when={hasSelectedDuplicates}>
            <ParagraphSmall marginBottom="0" color={Tokens.sys.content.contentNegative}>
              Rename duplicate pipelines before importing
            </ParagraphSmall>
          </Show>
          <Button
            kind="primary"
            type="button"
            disabled={isPending || hasSelectedDuplicates || selected.size === 0}
            startEnhancer={isPending ? <ProgressBarCircular size="xsmall" /> : undefined}
            onClick={() => mutate()}
          >
            Import
          </Button>
        </div>
      </div>
    </>
  );
}

export function Import() {
  const [css] = useStyletron();
  const [currentStep, dispatchStep] = useSteps(['Import', 'Review']);
  const navigate = useNavigate();
  const resetImports = useSetAtom(resetPipelineImportsAtom);
  const setRenamed = useSetAtom(pipelinesRenamedAtom);
  const dispatchSelection = useSetAtom(pipelineSelectionAtom);

  const reset = useCallback(() => {
    resetImports();
    setRenamed(new Map());
    dispatchSelection({ type: 'reset' });
    dispatchStep('reset');
  }, [dispatchSelection, dispatchStep, resetImports, setRenamed]);

  useEffect(() => () => reset(), [reset]);

  const sectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: Tokens.ref.sizing.scale400,
    padding: Tokens.ref.sizing.scale600,
    borderBottom: `1px solid ${Tokens.sys.border.borderOpaque}`,
  });

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        reset();
        navigate('..');
      }}
      overrides={{
        Close: {
          component: () => null,
        },
        Dialog: {
          style: {
            height: '900px',
            width: '800px',
            display: 'grid',
            gridTemplateRows: 'auto minmax(0, 1fr) auto',
          },
        },
      }}
    >
      <div className={sectionStyles}>
        <HeadingSmall marginBottom="0">Import Pipelines</HeadingSmall>
        <ParagraphSmall marginBottom="0">
          Select source objects to load pipelines, then either download them, choose a target instance to import to, or
          do both.
        </ParagraphSmall>
      </div>

      <StepsContext.Provider value={[currentStep, dispatchStep]}>
        <Show when={currentStep === 'Import'}>
          <Upload />
        </Show>
        <Show when={currentStep === 'Review'}>
          <Review />
        </Show>
      </StepsContext.Provider>
    </Modal>
  );
}
